import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Tag, Button, Spin, message, Divider, Descriptions, Modal } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { QRCode } from 'antd';
import { orderAPI, paymentsAPI } from '../services/api';
import { requireAuth } from '../utils/auth';
import './OrderDetail.css';

function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrVisible, setQrVisible] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);

  useEffect(() => {
    if (!requireAuth(navigate)) return;
    loadOrder();
    return () => {
      // 组件卸载时清除轮询
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [id]);

  const loadOrder = async () => {
    setLoading(true);
    try {
      const res = await orderAPI.getOrderById(id);
      setOrder(res.data);
    } catch (error) {
      message.error('加载订单失败');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setPaymentLoading(true);
    setQrVisible(true);
    try {
      console.log('[订单支付] 开始创建支付，订单ID:', id);
      const res = await paymentsAPI.createOrderPayment({ order_id: id });
      console.log('[订单支付] 支付创建成功:', res);
      setPaymentUrl(res.data.payment_url);

      // 开启轮询检测支付状态
      startPolling(res.data.order_no);
    } catch (error) {
      console.error('[订单支付] 支付创建失败:', error);
      message.error(error.message || '获取支付链接失败');
      setPaymentUrl('');
      setQrVisible(false);
    } finally {
      setPaymentLoading(false);
    }
  };

  const startPolling = (orderNo) => {
    // 清除旧的轮询
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    console.log('[订单轮询] 开始轮询订单:', orderNo);
    let pollCount = 0;
    const maxPolls = 100; // 最多轮询100次（5分钟）

    const interval = setInterval(async () => {
      pollCount++;
      console.log(`[订单轮询] 第${pollCount}次轮询，订单号:`, orderNo);

      try {
        const res = await paymentsAPI.getOrderPaymentStatus(orderNo);
        console.log(`[订单轮询] 第${pollCount}次轮询结果:`, res);

        if (res.data.is_paid) {
          // 支付成功
          console.log('[订单轮询] 检测到支付成功！');
          clearInterval(interval);
          setPollingInterval(null);
          message.success('支付成功！');
          setQrVisible(false);

          // 重新加载订单
          loadOrder();
        } else {
          console.log(`[订单轮询] 第${pollCount}次检测，订单状态: ${res.data.status}，未支付`);
        }
      } catch (error) {
        console.error('[订单轮询] 轮询订单状态失败:', error);
      }

      // 达到最大轮询次数，停止轮询
      if (pollCount >= maxPolls) {
        console.log('[订单轮询] 达到最大轮询次数，停止轮询');
        clearInterval(interval);
        setPollingInterval(null);
        message.info('支付超时，请刷新页面查看');
      }
    }, 3000); // 每3秒轮询一次

    setPollingInterval(interval);
  };

  const handleClosePayment = () => {
    setQrVisible(false);
    setPaymentUrl('');

    // 停止轮询
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  const handleCancelOrder = async () => {
    try {
      await orderAPI.cancelOrder(id);
      message.success('订单已取消');
      loadOrder();
    } catch (error) {
      message.error('取消订单失败');
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'orange', text: '待支付' },
      paid: { color: 'blue', text: '已支付' },
      shipped: { color: 'cyan', text: '已发货' },
      completed: { color: 'green', text: '已完成' },
      cancelled: { color: 'red', text: '已取消' }
    };
    return statusMap[status] || statusMap.pending;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const statusInfo = getStatusTag(order.status);

  return (
    <div className="order-detail-container">
      <div className="detail-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/orders')}
        >
          返回订单列表
        </Button>
      </div>

      <Card title="订单详情" className="order-card">
        <Descriptions column={2} bordered>
          <Descriptions.Item label="订单号" span={2}>
            {order.order_no}
          </Descriptions.Item>
          <Descriptions.Item label="订单状态">
            <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="订单金额">
            <span className="order-amount">¥{order.total_amount}</span>
          </Descriptions.Item>
          <Descriptions.Item label="下单时间" span={2}>
            {new Date(order.created_at).toLocaleString('zh-CN')}
          </Descriptions.Item>
          {order.pay_time && (
            <Descriptions.Item label="支付时间" span={2}>
              {new Date(order.pay_time).toLocaleString('zh-CN')}
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* 操作按钮 */}
        <div className="order-actions" style={{ marginTop: 16 }}>
          {order.status === 'pending' && (
            <>
              <Button
                type="primary"
                size="large"
                onClick={handlePayment}
                loading={paymentLoading}
              >
                立即支付
              </Button>
              <Button
                size="large"
                onClick={handleCancelOrder}
                style={{ marginLeft: 8 }}
              >
                取消订单
              </Button>
            </>
          )}
        </div>

        <Divider />

        {/* 收货信息 - EDI审核验证点 */}
        <h3>收货信息</h3>
        <Descriptions column={1} bordered className="address-info">
          <Descriptions.Item label="收货人">
            {order.receiver_name}
          </Descriptions.Item>
          <Descriptions.Item label="联系电话">
            {order.receiver_phone}
          </Descriptions.Item>
          <Descriptions.Item label="收货地址">
            {order.receiver_address}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        {/* 商品清单 */}
        <h3>商品清单</h3>
        <div className="product-list">
          {order.items && order.items.map(item => (
            <div key={item.id} className="product-item">
              <img
                src={item.product_image || '/images/default-product.jpg'}
                alt={item.product_name}
                onError={(e) => { e.target.src = '/images/default-product.jpg'; }}
              />
              <div className="product-info">
                <div className="product-name">{item.product_name}</div>
                <div className="product-price">单价: ¥{item.price}</div>
              </div>
              <div className="product-quantity">x {item.quantity}</div>
              <div className="product-subtotal">¥{item.subtotal}</div>
            </div>
          ))}
        </div>

        <div className="order-total">
          <span>订单总计:</span>
          <span className="total-amount">¥{order.total_amount}</span>
        </div>
      </Card>

      {/* 支付二维码弹窗 */}
      <Modal
        title="扫码支付"
        open={qrVisible}
        onCancel={handleClosePayment}
        footer={null}
        centered
        width={400}
      >
        <div className="payment-modal">
          <div className="qr-code-container">
            {paymentLoading ? (
              <Spin tip="生成支付链接中..." />
            ) : paymentUrl ? (
              <>
                <QRCode value={paymentUrl} size={240} />
                <p style={{ marginTop: 16, textAlign: 'center', color: '#1890ff' }}>
                  正在等待支付...
                </p>
              </>
            ) : (
              <p>未获取到支付链接，请稍后再试。</p>
            )}
          </div>
          <div className="payment-info">
            <h3>订单号：{order.order_no}</h3>
            <div className="payment-price">
              <span className="price-label">支付金额：</span>
              <span className="price-value">¥{order.total_amount}</span>
            </div>
            <p className="payment-tip">请使用微信或支付宝扫码支付</p>
            <p className="payment-tip">支付成功后将自动更新订单状态</p>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default OrderDetail;
